import apiClient from './client';
import { SpeechBubbleStyle } from '../utils/speechStyles';

export interface AdminUser {
  id: number;
  email: string;
  is_verified: boolean;
  is_admin: boolean;
  created_at: string;
  max_points: number;
  oauth_provider: string;
  point_count: number;
  move_count: number;
}

export interface DayStat {
  day: string;
  moves: number;
  creates: number;
  deletes: number;
}

export interface RecentEvent {
  id: number;
  event_type: string;
  point_id: number;
  user_id: number;
  user_email: string;
  x: number;
  y: number;
  old_x?: number;
  old_y?: number;
  color: string;
  name: string;
  created_at: string;
}

export interface ImportResult {
  created: number;
  skipped: number;
}

export const getAdminUsers = (search = '', limit = 50, offset = 0) =>
  apiClient.get<{ users: AdminUser[]; total: number }>('/admin/users', {
    params: { search, limit, offset },
  });

export const updateAdminUser = (id: number, max_points: number, is_admin: boolean) =>
  apiClient.put(`/admin/users/${id}`, { max_points, is_admin });

export const deleteAdminUser = (id: number) =>
  apiClient.delete(`/admin/users/${id}`);

export const getAdminStats = (days = 30) =>
  apiClient.get<{ stats: DayStat[] }>('/admin/stats', { params: { days } });

export const getAdminEvents = (limit = 50) =>
  apiClient.get<{ events: RecentEvent[] }>('/admin/events', { params: { limit } });

export const importImage = (formData: FormData) =>
  apiClient.post<ImportResult>('/admin/import-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export interface AdminZone {
  id: number;
  user_id: number;
  user_email: string;
  name: string;
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
  eviction_timeout_sec: number;
  expires_at: string | null;
  is_free: boolean;
  amount_rub: number;
  created_at: string;
}

export const getAdminZones = (search = '', limit = 50, offset = 0) =>
  apiClient.get<{ zones: AdminZone[]; total: number }>('/admin/zones', {
    params: { search, limit, offset },
  });

export const adminDeleteZone = (id: number) =>
  apiClient.delete(`/admin/zones/${id}`);

export interface AdminCreateZoneRequest {
  user_id: number;
  name: string;
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
  eviction_timeout_sec: number;
  is_free: boolean;
  expires_at: string | null; // ISO datetime string, null if free
}

export const adminCreateZone = (req: AdminCreateZoneRequest) =>
  apiClient.post<AdminZone>('/admin/zones', req);

export const adminUpdateZone = (id: number, req: AdminCreateZoneRequest) =>
  apiClient.put<AdminZone>(`/admin/zones/${id}`, req);

export interface MazeResult {
  zone_id: number;
  walls_created: number;
  entrance_x: number;
  entrance_y: number;
  exit_x: number;
  exit_y: number;
  animated: boolean;
}

export const generateMaze = (params: {
  name: string;
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
  wall_color: string;
  pace_ms?: number;
}) => apiClient.post<MazeResult>('/admin/generate-maze', params);

export interface AdminPoint {
  id: number;
  user_id: number;
  user_email: string;
  owner_has_subscription: boolean;
  name: string;
  x: number;
  y: number;
  color: string;
  is_impassable: boolean;
  created_at: string;
  updated_at: string;
}

/** Who occupies a world cell — 404 when the cell is empty. */
export const getAdminPointAt = (x: number, y: number) =>
  apiClient.get<{ point: AdminPoint }>('/admin/points/at', { params: { x, y } });

export const getAdminPoints = (search = '', limit = 50, offset = 0) =>
  apiClient.get<{ points: AdminPoint[]; total: number }>('/admin/points', {
    params: { search, limit, offset },
  });

export const adminCreatePoint = (payload: {
  user_id: number;
  name: string;
  x: number;
  y: number;
}) => apiClient.post<AdminPoint>('/admin/points', payload);

export const adminUpdatePoint = (id: number, payload: {
  name: string;
  x: number;
  y: number;
}) => apiClient.put<AdminPoint>(`/admin/points/${id}`, payload);

export const adminEmitDialog = (payload: {
  point_id: number;
  text?: string;
  jump?: boolean;
  style?: SpeechBubbleStyle;
}) => apiClient.post<{ ok: boolean }>('/admin/dialog/emit', payload);

export interface ConnectionStats {
  total: number;
  players: number;
  observers: number;
}

export interface ConnectionInfo {
  user_id: number;
  point_id: number;
  remote_addr: string;
  connected_at: string;
  duration_sec: number;
  area_x_min: number;
  area_y_min: number;
  area_x_max: number;
  area_y_max: number;
  is_observer: boolean;
}

export const getConnectionStats = () =>
  apiClient.get<ConnectionStats>('/admin/connections/stats');

export const getConnections = (limit = 100, offset = 0) =>
  apiClient.get<{ connections: ConnectionInfo[]; total: number }>(
    '/admin/connections',
    { params: { limit, offset } },
  );
